import React from 'react'
import { useIntl } from 'react-intl'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import { Box, TableHead } from '@mui/material'
import TablePaginationActions from '../../../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import EditIcon from '../../../../../../assets/icons/Edit/Edit.svg'
import ConfirmDelete from '../../../../../../components/dialogs/ConfirmDelete'
import { formatDateToCalendarDate } from '../../../../../../utils/functions'
import { CoursePromotionState } from '../../../../../../features/types'

interface Props {
    promotions: CoursePromotionState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
    onEdit: (row: CoursePromotionState) => void
    onDelete: (id: number) => void
}

const styles = {
    tableContainer: {
        border: 1,
        borderColor: 'grey.100',
        borderRadius: 2,
    },
    table: {
        minWidth: 500,
    },
    tableHead: {
        bgcolor: 'grey.100',
    },
    flexEnd: {
        display: 'flex',
        justifyContent: 'end',
    },
    iconButton: {
        background: '#f3f4f5',
        mr: 2,
    },
}

const PromoTable: React.FC<Props> = ({
    promotions = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    isFetching = false,
    setPage,
    setRowsPerPage,
    onEdit,
    onDelete,
}) => {
    const { formatMessage } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 1 ? Math.max(0, rowsPerPage - promotions?.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={styles.tableContainer}
                elevation={0}
            >
                <Table
                    sx={styles.table}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={styles.tableHead}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'sloganKz' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'sloganRu' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'startDate' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'endDate' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'packageSales' })}(%)
                            </TableCell>

                            <TableCell
                                align="right"
                                width={12}
                            >
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {promotions.length
                            ? promotions.map((row, index) => (
                                  <TableRow key={row.id}>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {page * rowsPerPage + index + 1}.
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row?.sloganKz?.substring(0, 15)}
                                          {row?.sloganKz?.length > 15 && '...'}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row?.sloganRu?.substring(0, 15)}
                                          {row?.sloganRu?.length > 15 && '...'}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {formatDateToCalendarDate(
                                              row?.startTime,
                                              '.',
                                              ['d', 'mm', 'yyyy'],
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {formatDateToCalendarDate(
                                              row?.endTime,
                                              '.',
                                              ['d', 'mm', 'yyyy'],
                                          )}
                                      </TableCell>

                                      <TableCell
                                          component="th"
                                          scope="row"
                                      >
                                          {row?.discount}%
                                      </TableCell>

                                      <TableCell
                                          style={styles.flexEnd}
                                          align="right"
                                      >
                                          <ConfirmDelete
                                              confirmAction={() =>
                                                  onDelete(row?.id)
                                              }
                                          />

                                          <IconButton
                                              sx={styles.iconButton}
                                              onClick={() => onEdit(row)}
                                          >
                                              <img
                                                  src={EditIcon}
                                                  alt="edit"
                                              />
                                          </IconButton>
                                      </TableCell>
                                  </TableRow>
                              ))
                            : !isFetching && (
                                  <TableRow>
                                      <TableCell
                                          component="th"
                                          scope="row"
                                          align="center"
                                          colSpan={10}
                                      >
                                          {formatMessage({ id: 'noData' })}
                                      </TableCell>
                                  </TableRow>
                              )}

                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={8} />
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={8}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default PromoTable
